import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";

const ROW_HEIGHT = 44; // px ~ per row (matches padding below)
const VISIBLE_BODY_ROWS = 10; // how many body rows you want visible by default

const TableWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ScrollContainer = styled.div`
  /* height = header (~ROW_HEIGHT) + desired visible body rows * ROW_HEIGHT */
  max-height: ${ROW_HEIGHT * (VISIBLE_BODY_ROWS + 1)}px;
  overflow-y: auto;
  /* make sticky header behave nicely over scrolling */
  position: relative;
  border: 1px solid #ddd;
  border-radius: 6px;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
  width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover { background-color: #155e36; }
  svg { margin-right: 6px; }
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px;
    /* ensure consistent row height visually close to ROW_HEIGHT */
    line-height: 1.2;
  }

  th:nth-child(1), td:nth-child(1) { width: 36%; }
  th:nth-child(2), td:nth-child(2) { width: 20%; } /* NIP */
  th:nth-child(3), td:nth-child(3) { width: 22%; }
  th:nth-child(4), td:nth-child(4) { width: 22%; }

  th {
    background: #87ceeb;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) { background-color: #f9f9f9; }
  tbody tr:hover { background-color: #eef6ff; }
`;

const StatusText = styled.span`
  font-weight: bold;
  color: ${({ months }) => (months >= 6 ? "red" : "#333")};
`;

const InactiveCustomers = ({ salesData }) => {
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [filterText, setFilterText] = useState("");

  const ymKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

  useEffect(() => {
    if (!salesData || salesData.length === 0) return;

    // last purchase per customer (name + NIP)
    const lastPurchases = {};
    salesData.forEach((sale) => {
      const customer = sale.NazwaKontrahenta || "Nieznany Kontrahent";
      const nip = (sale.NrKontrahenta || "").toString().trim();
      const year = parseInt(sale.Rok, 10);
      const month = parseInt(sale.Miesiac, 10);
      if (isNaN(year) || isNaN(month)) return;

      const key = ymKey(year, month);
      const customerKey = `${customer}||${nip}`;
      if (!lastPurchases[customerKey] || lastPurchases[customerKey] < key) {
        lastPurchases[customerKey] = key;
      }
    });

    // latest month in dataset
    const latestKey = salesData.reduce((latest, sale) => {
      const y = parseInt(sale.Rok, 10);
      const m = parseInt(sale.Miesiac, 10);
      if (isNaN(y) || isNaN(m)) return latest;
      const k = ymKey(y, m);
      return !latest || latest < k ? k : latest;
    }, null);

    if (!latestKey) return;
    const [latestYear, latestMonth] = latestKey.split("-").map(Number);

    const list = Object.entries(lastPurchases).map(([key, lastDate]) => {
      const [customer, nip] = key.split("||");
      const [lastYear, lastMonth] = lastDate.split("-").map(Number);
      const monthsDiff = (latestYear - lastYear) * 12 + (latestMonth - lastMonth);
      return {
        customer,
        nip,
        lastPurchase: lastDate,
        monthsInactive: monthsDiff,
      };
    });

    // only 6+ months inactive, sort by longest inactivity then name
    const filtered = list
      .filter((c) => c.monthsInactive >= 6)
      .sort((a, b) => b.monthsInactive - a.monthsInactive || a.customer.localeCompare(b.customer));

    setInactiveCustomers(filtered);
  }, [salesData]);

  const displayedCustomers = inactiveCustomers.filter((item) => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return true;
    return (
      item.customer.toLowerCase().includes(needle) ||
      (item.nip || "").toLowerCase().includes(needle)
    );
  });

  const exportToExcel = () => {
    // CSV (Excel-friendly, UTF-8 with BOM) — exports ALL filtered rows
    const headers = ["Kontrahent", "NIP", "Ostatni zakup (YYYY-MM)", "Miesiące braku zakupów"];
    const rows = displayedCustomers.map((r) => [
      r.customer,
      r.nip || "",
      r.lastPurchase,
      r.monthsInactive,
    ]);

    const sep = ";";
    const escape = (v) => {
      const s = String(v ?? "");
      return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csv = [headers, ...rows].map((row) => row.map(escape).join(sep)).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });

    const now = new Date();
    const fname = `nieaktywni_klienci_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <TableWrapper>
      <TitleWrapper>
        Klienci którzy nie kupili od 6 miesięcy
        <div data-tooltip-id="inactive-tooltip" style={{ cursor: "pointer" }}>
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="inactive-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Analiza nieaktywnych klientów</strong><br/>
            Lista pokazuje klientów, którzy nie dokonali zakupu od <b>6 miesięcy lub dłużej</b>.<br/>
            Szukaj po nazwie lub NIP. Eksport dostępny do pliku CSV (Excel).
          </>
        }
      />

      <ControlsRow>
        <div>
          <label>Kontrahent / NIP: </label>
          <Input
            type="text"
            placeholder="Wpisz nazwę lub NIP…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </ControlsRow>

      <ScrollContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Kontrahent</th>
              <th>NIP</th>
              <th>Ostatni zakup</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {displayedCustomers.length > 0 ? (
              displayedCustomers.map((item, index) => (
                <tr key={`${item.customer}-${item.nip}-${index}`}>
                  <td>{item.customer}</td>
                  <td>{item.nip || "—"}</td>
                  <td>{item.lastPurchase}</td>
                  <td>
                    <StatusText months={item.monthsInactive}>
                      Brak zakupów {item.monthsInactive}+ miesięcy
                    </StatusText>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">Brak klientów dla tego filtra.</td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </ScrollContainer>
    </TableWrapper>
  );
};

export default InactiveCustomers;
